import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import api from '../api';

const categoryConfig = {
  'Travel Tips':        { color: '#3F72AF', icon: '✈️' },
  'Visa Updates':       { color: '#16a34a', icon: '🛂' },
  'Destination Guides': { color: '#d97706', icon: '🗺️' },
  'Travel News':        { color: '#7c3aed', icon: '📰' },
  'General':            { color: '#6b7280', icon: '💬' }
};

const categories = ['All', 'Travel Tips', 'Visa Updates', 'Destination Guides', 'Travel News'];

function BlogCard({ post }) {
  const [hovered, setHovered] = useState(false);
  var catConfig = categoryConfig[post.category] || categoryConfig['General'];

  return (
    <Link
      to={'/blog/' + post.slug}
      style={{ textDecoration: 'none', display: 'block' }}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
    >
      <div style={{
        borderRadius: 16, overflow: 'hidden', background: '#fff',
        boxShadow: hovered
          ? '0 20px 48px rgba(17,45,78,0.16)'
          : '0 4px 20px rgba(17,45,78,0.08)',
        transform: hovered ? 'translateY(-5px)' : 'translateY(0)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        height: '100%', display: 'flex', flexDirection: 'column'
      }}>

        {/* Image */}
        <div style={{ position: 'relative', overflow: 'hidden', height: 200, flexShrink: 0 }}>
          {post.coverImage ? (
            <img
              src={post.coverImage}
              alt={post.title}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transform: hovered ? 'scale(1.06)' : 'scale(1)',
                transition: 'transform 0.5s ease'
              }}
            />
          ) : (
            // Placeholder when no cover image
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(135deg, ' + catConfig.color + '22, ' + catConfig.color + '44)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '3rem'
            }}>
              {catConfig.icon}
            </div>
          )}

          {/* Category badge */}
          <div style={{
            position: 'absolute', top: 14, left: 14,
            background: catConfig.color,
            borderRadius: 20, padding: '4px 12px',
            fontSize: '0.7rem', fontWeight: 700,
            color: '#fff', letterSpacing: '0.05em',
            display: 'flex', alignItems: 'center', gap: 5
          }}>
            <span style={{ fontSize: '0.75rem' }}>{catConfig.icon}</span>
            {post.category}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 20px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Date */}
          <p style={{ margin: '0 0 10px', fontSize: '0.75rem', opacity: 0.45, fontWeight: 600 }}>
            {new Date(post.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>

          {/* Title */}
          <h3 style={{
            margin: '0 0 10px',
            fontSize: '1rem', lineHeight: 1.4,
            color: 'var(--bj-espresso)',
            transition: 'color 0.2s'
          }}>
            {post.title}
          </h3>

          {/* Excerpt */}
          {post.excerpt && (
            <p style={{
              margin: '0 0 16px', fontSize: '0.87rem',
              opacity: 0.65, lineHeight: 1.65, flex: 1,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical'
            }}>
              {post.excerpt}
            </p>
          )}

          {/* Read more CTA */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 'auto',
            color: catConfig.color, fontWeight: 700,
            fontSize: '0.83rem',
            paddingTop: 14,
            borderTop: '1px solid var(--bj-sand)'
          }}>
            Read article
            <span style={{
              display: 'inline-block',
              transform: hovered ? 'translateX(4px)' : 'translateX(0)',
              transition: 'transform 0.25s ease'
            }}>→</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { t } = useTranslation();
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');

  useEffect(function() {
    setLoading(true);
    var query = category !== 'All' ? '?category=' + encodeURIComponent(category) : '';
    api.get('/blog' + query)
      .then(function(res) { setPosts(res.data.posts); })
      .catch(function() { setPosts([]); })
      .finally(function() { setLoading(false); });
  }, [category]);

  return (
    <Layout>
      {/* HERO BAND */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bj-espresso) 0%, #1e3f6b 100%)',
        padding: '64px 0 56px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(63,114,175,0.1)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>
          <span className="eyebrow" style={{ color: 'var(--bj-sand)' }}>Travel Updates</span>
          <h1 style={{ color: '#fff', margin: '10px 0 14px', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
            Blog
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 520, lineHeight: 1.75, margin: 0 }}>
            Travel tips, visa updates, destination guides, and news from our team of specialists on the ground.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">

          {/* CATEGORY FILTERS */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 36, flexWrap: 'wrap' }}>
            {categories.map(function(c) {
              var isActive = c === category;
              var conf     = c === 'All' ? null : categoryConfig[c];
              return (
                <button
                  key={c}
                  onClick={function() { setCategory(c); }}
                  style={{
                    padding: '8px 18px', borderRadius: 20,
                    border: '1.5px solid ' + (isActive ? (conf ? conf.color : 'var(--bj-espresso)') : 'var(--bj-sand)'),
                    background: isActive ? (conf ? conf.color + '18' : 'var(--bj-espresso)') : 'transparent',
                    color: isActive ? (conf ? conf.color : '#fff') : 'var(--bj-espresso)',
                    fontFamily: 'var(--font-body)', fontWeight: 700,
                    fontSize: '0.83rem', cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  {conf && <span style={{ fontSize: '0.8rem' }}>{conf.icon}</span>}
                  {c}
                </button>
              );
            })}
          </div>

          {/* POSTS */}
          {loading ? <Loader /> : posts.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '56px 24px',
              background: '#fff', borderRadius: 14,
              boxShadow: '0 4px 20px rgba(17,45,78,0.06)'
            }}>
              <span style={{ fontSize: '2rem', display: 'block', marginBottom: 12 }}>📝</span>
              <h3 style={{ margin: '0 0 8px' }}>No posts yet in this category</h3>
              <p style={{ opacity: 0.6, margin: 0 }}>Check back soon — our team publishes regularly.</p>
            </div>
          ) : (
            <div className="grid-3">
              {posts.map(function(p) {
                return <BlogCard key={p._id} post={p} />;
              })}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}